import { isAxiosError } from 'axios';
import { FileTooLargeError } from '@/api/uploads.api';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';
import { getApiErrorMessage } from '@/lib/utils/error.utils';

export interface UploadErrorMessages {
  /** Shown when the file is over the server-declared size cap. */
  tooLarge: string;
  /** Shown when the failure carries no usable reason (e.g. storage refused the PUT). */
  fallback: string;
}

/**
 * Pure: the message for a failed upload, saying WHY it failed (UX round 2,
 * MYN-271). Every uploader used to show one fixed "try again" line, so a file
 * that never left the browser (blocked by the storage bucket's CORS rules, or
 * offline) read the same as a bad file type, and users kept retrying valid
 * files.
 *
 * Order: our own size check, then no response at all (network or CORS - the
 * file never reached storage), then the API's own reason (a refused presign,
 * e.g. an unsupported type), then the caller's fallback.
 *
 * @param error    Unknown value thrown by `uploadFile`.
 * @param messages The caller's size and fallback copy.
 * @returns A user-facing message, never empty.
 */
export function getUploadErrorMessage(error: unknown, messages: UploadErrorMessages): string {
  if (error instanceof FileTooLargeError) return messages.tooLarge;
  if (isAxiosError(error) && !error.response) return COMMON_STRINGS.UPLOAD.NETWORK_ERROR;
  return getApiErrorMessage(error, messages.fallback);
}
